import { Button, Form, Input, Modal, Radio } from 'antd';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import s from './EditTaskModal.module.scss';

import type { FC } from 'react';
import type { IEditTaskPayload } from 'pages/TasksDashboard/types';

interface IEditTaskModalProps {
  open: boolean;
  isSubmitting: boolean;
  submitError: boolean;
  patientName: string;
  initialValues: IEditTaskPayload & { title: string };
  onSubmit: (payload: IEditTaskPayload) => void;
  onCancel: () => void;
}

const TITLE_MAX_LENGTH = 100;

const EditTaskModal: FC<IEditTaskModalProps> = ({
  open,
  isSubmitting,
  submitError,
  patientName,
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<IEditTaskPayload & { title: string }>();

  useEffect(() => {
    if (open) {
      form.setFieldsValue(initialValues);
    }
  }, [open, initialValues, form]);

  const title = Form.useWatch('title', form);
  const titleErrors = Form.useWatch(() => form.getFieldError('title'), form);
  const isSaveDisabled = !title?.trim() || (titleErrors?.length ?? 0) > 0;

  const handleFinish = useCallback(
    (values: IEditTaskPayload & { title: string }) => {
      onSubmit({
        title: values.title,
        priority: values.priority,
      });
    },
    [onSubmit],
  );

  return (
    <Modal
      open={open}
      title={t('tasksDashboard.editModal.title')}
      centered
      destroyOnClose
      maskClosable={false}
      onCancel={onCancel}
      footer={null}
      data-cy="edit-task-modal"
    >
      <div className={s['modal-body']}>
        {submitError && (
          <p className={s['inline-error']} data-cy="edit-modal-submit-error">
            {t('tasksDashboard.editError')}
          </p>
        )}

        <Form
          form={form}
          layout="vertical"
          onFinish={handleFinish}
          initialValues={initialValues}
        >
          <Form.Item label={t('tasksDashboard.editModal.patientField')}>
            <Input value={patientName} disabled data-cy="edit-task-patient" />
          </Form.Item>

          <Form.Item
            name="title"
            label={t('tasksDashboard.editModal.taskField')}
            rules={[
              {
                required: true,
                whitespace: true,
                message: t('tasksDashboard.editModal.titleRequired'),
              },
              {
                max: TITLE_MAX_LENGTH,
                message: t('tasksDashboard.editModal.titleMaxLength'),
              },
              {
                pattern: /\w{2,}/,
                message: t('tasksDashboard.editModal.titleInvalid'),
              },
            ]}
          >
            <Input.TextArea
              rows={2}
              maxLength={TITLE_MAX_LENGTH}
              showCount
              autoFocus
              data-cy="edit-task-title"
            />
          </Form.Item>

          <Form.Item
            name="priority"
            label={t('tasksDashboard.editModal.priorityField')}
            rules={[
              {
                required: true,
                message: t('tasksDashboard.editModal.priorityRequired'),
              },
            ]}
          >
            <Radio.Group
              className={s['priority-radio']}
              data-cy="edit-task-priority"
            >
              <Radio value="high">{t('tasksDashboard.priority.high')}</Radio>
              <Radio value="medium">
                {t('tasksDashboard.priority.medium')}
              </Radio>
              <Radio value="low">{t('tasksDashboard.priority.low')}</Radio>
            </Radio.Group>
          </Form.Item>

          <div className={s['modal-footer']}>
            <Button onClick={onCancel} data-cy="edit-task-cancel-btn">
              {t('tasksDashboard.editModal.cancel')}
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isSubmitting}
              disabled={isSubmitting || isSaveDisabled}
              data-cy="edit-task-submit-btn"
            >
              {t('tasksDashboard.editModal.save')}
            </Button>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default EditTaskModal;
